import { useState, useRef, useEffect } from 'react';
import { DEFAULT } from './default';
import { useDebugTheme } from './useDebugTheme';
import { useScenariosList } from '../../hooks/Scenarios/useScenariosList';
import { useDeleteScenario } from '../../hooks/Scenarios/useDeleteScenario';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { RxCross1 } from 'react-icons/rx';

interface ScenarioLoaderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoad: (scenarioText: string) => void;
}

export const ScenarioLoaderModal = ({
  isOpen,
  onClose,
  onLoad,
}: ScenarioLoaderModalProps) => {
  const { isDark } = useDebugTheme();
  const { mutateAsync: deleteScenario } = useDeleteScenario();
  const [scenarioText, setScenarioText] = useState(DEFAULT);
  const [selectedScenarioName, setSelectedScenarioName] = useState<string>('');
  const [selectedScenarioDescription, setSelectedScenarioDescription] =
    useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showJsonTextarea, setShowJsonTextarea] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: scenarios, isLoading, error } = useScenariosList();

  // Filter scenarios based on search term
  const filteredScenarios =
    scenarios?.filter(
      (scenario) =>
        scenario.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (scenario.description &&
          scenario.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase())),
    ) || [];

  const handleLoad = () => {
    onLoad(scenarioText);
    onClose();
  };

  const handleClose = () => {
    setScenarioText(DEFAULT);
    setSelectedScenarioName('');
    setSelectedScenarioDescription('');
    setSearchTerm('');
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
    onClose();
  };

  const handleClear = () => {
    setScenarioText(DEFAULT);
    setSelectedScenarioName('');
    setSelectedScenarioDescription('');
    setSearchTerm('');
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const handleScenarioSelect = (scenarioName: string) => {
    const scenario = scenarios?.find((s) => s.name === scenarioName);
    if (scenario) {
      setScenarioText(JSON.stringify(scenario, null, 2));
      setSelectedScenarioName(scenario.name);
      setSelectedScenarioDescription(scenario.description || '');
      setSearchTerm('');
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const handleScenarioDelete = (scenarioId: string) => {
    deleteScenario(scenarioId);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsDropdownOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => setIsDropdownOpen(false), 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isDropdownOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredScenarios.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredScenarios[highlightedIndex]) {
          handleScenarioSelect(filteredScenarios[highlightedIndex].name);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear selected scenario when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedScenarioName('');
      setSelectedScenarioDescription('');
      setSearchTerm('');
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
      setScenarioText(DEFAULT);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className={`max-w-4xl max-h-[90dvh] rounded-lg ${
          isDark
            ? 'bg-slate-900 shadow-2xl shadow-black/50'
            : 'bg-white shadow-2xl shadow-black/20'
        } border-0`}
      >
        {/* Header */}
        <div
          className={`flex items-start justify-between rounded-t border-b p-5 ${
            isDark ? 'border-slate-700' : 'border-slate-200'
          }`}
        >
          <h3
            className={`text-xl font-medium ${
              isDark ? 'text-slate-100' : 'text-slate-900'
            }`}
          >
            Load Scenario
          </h3>
          <DialogClose
            className={`ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm cursor-pointer ${
              isDark
                ? 'text-slate-400 hover:bg-slate-700 hover:text-slate-100'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
            }`}
            onClick={handleClose}
          >
            <RxCross1 className="h-5 w-5" />
          </DialogClose>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-4 min-h-64">
            <div>
              <label className="block text-sm font-medium text-[var(--dbg-text-muted)] mb-2">
                Search Scenario
              </label>
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading}
                  placeholder={
                    isLoading ? 'Loading scenarios...' : 'Search scenarios...'
                  }
                  className="block w-full rounded-lg border border-[var(--dbg-border-input)] bg-[var(--dbg-bg-secondary)] p-2.5 text-sm text-[var(--dbg-text)] placeholder:text-[var(--dbg-text-dim)] focus:border-blue-400 focus:ring-blue-400 disabled:opacity-50"
                />
                {isDropdownOpen && filteredScenarios.length > 0 && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-10 w-full mt-1 bg-[var(--dbg-bg-secondary)] border border-[var(--dbg-border)] rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  >
                    {filteredScenarios.map((scenario, index) => (
                      <div
                        key={scenario.id}
                        onClick={() => handleScenarioSelect(scenario.name)}
                        className={`flex justify-between px-3 py-2 text-sm cursor-pointer hover:bg-[var(--dbg-hover)] ${
                          index === highlightedIndex ? 'bg-blue-900/30' : ''
                        }`}
                      >
                        <div className="font-medium text-[var(--dbg-text)]">
                          {scenario.name}
                        </div>
                        {scenario.description && (
                          <div className="text-[var(--dbg-text-secondary)] text-xs mt-1">
                            {scenario.description.split('---')[0]}
                          </div>
                        )}
                        <div
                          className="flex items-center p-1 text-[var(--dbg-text-dim)] hover:text-red-400"
                          onClick={() => handleScenarioDelete(scenario.id)}
                        >
                          <button className="text-sm z-10">X</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {isDropdownOpen &&
                  searchTerm &&
                  filteredScenarios.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-[var(--dbg-bg-secondary)] border border-[var(--dbg-border)] rounded-lg shadow-lg p-3 text-sm text-[var(--dbg-text-secondary)]">
                      No scenarios found matching &quot;{searchTerm}&quot;
                    </div>
                  )}
              </div>
              {error ? (
                <p className="text-red-400 text-sm mt-1">
                  Error loading scenarios. Please try again.
                </p>
              ) : null}
            </div>

            {/* Selected Scenario Info */}
            {selectedScenarioName && (
              <div className="bg-[var(--dbg-bg-secondary)] p-3 rounded border border-[var(--dbg-border)]">
                <div className="space-y-2">
                  <div className="text-sm">
                    <span className="font-bold text-[var(--dbg-text)]">
                      {selectedScenarioName}
                    </span>
                  </div>
                  {selectedScenarioDescription && (
                    <div className="text-sm text-[var(--dbg-text-muted)]">
                      <pre>{selectedScenarioDescription}</pre>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <button
                type="button"
                onClick={() => setShowJsonTextarea(!showJsonTextarea)}
                className="flex items-center text-sm font-medium text-[var(--dbg-text-secondary)] hover:text-[var(--dbg-text)] mb-2"
              >
                <svg
                  className={`w-4 h-4 mr-1 transition-transform ${
                    showJsonTextarea ? 'rotate-90' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                Scenario JSON
              </button>
              {showJsonTextarea && (
                <textarea
                  rows={15}
                  value={scenarioText}
                  onChange={(e) => setScenarioText(e.target.value)}
                  placeholder="Paste your scenario JSON here or select a predefined scenario above..."
                  className={`block w-full rounded-lg border text-sm font-mono disabled:cursor-not-allowed disabled:opacity-50 focus:ring-blue-400 focus:border-blue-400 p-2.5 ${
                    isDark
                      ? 'border-slate-600 bg-slate-950 text-slate-100'
                      : 'border-slate-300 bg-white text-slate-900'
                  }`}
                />
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className={`flex items-center space-x-2 rounded-b p-6 border-t ${
            isDark ? 'border-slate-700' : 'border-slate-200'
          }`}
        >
          <button
            onClick={handleClear}
            className="w-1/2 border py-1 text-[#2C1696] focus:ring-0 focus:outline-hidden rounded-lg cursor-pointer h-10 text-center"
          >
            Clear
          </button>
          <button
            onClick={handleLoad}
            className="w-1/2 border py-1 bg-[#2C1696] text-white focus:ring-0 focus:outline-hidden rounded-lg cursor-pointer h-10 text-center"
          >
            Load Scenario
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
